import { Link } from "react-router-dom";
import type { ReactNode } from "react";
import type { MergedAccountView } from "@sales-ops-amplify/shared";
import { ExternalLinksList } from "@/components/external-links-editor";

function RailSection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="min-w-0 space-y-2 border-b border-border pb-4 last:border-b-0">
      <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
        {title}
      </h3>
      <div className="min-w-0">{children}</div>
    </section>
  );
}

export function CompanyDetailsRail({
  merged,
  description,
  industry,
  keywords,
  employeeCount,
  foundedYear,
  phone,
  domain,
  website,
  linkedinUrl,
  locationLabel,
}: {
  merged: MergedAccountView;
  description?: string;
  industry?: string;
  keywords?: string[];
  employeeCount?: number;
  foundedYear?: number;
  phone?: string;
  domain?: string;
  website?: string;
  linkedinUrl?: string;
  locationLabel?: string;
}) {
  const user = merged.user;
  const people = merged.people ?? [];
  const opps = merged.opportunities ?? [];

  return (
    <aside className="min-w-0 max-w-full space-y-4 overflow-hidden rounded-lg border border-border bg-card p-4 text-sm">
      <RailSection title="About">
        {description ? (
          <p className="break-words text-xs leading-relaxed text-muted-foreground line-clamp-6">
            {description}
          </p>
        ) : (
          <p className="text-muted-foreground">No description</p>
        )}
        {industry ? (
          <p className="mt-2 break-words text-xs">
            <span className="text-muted-foreground">Industry · </span>
            {industry}
          </p>
        ) : null}
        {keywords?.length ? (
          <div className="mt-2 flex flex-wrap gap-1">
            {keywords.slice(0, 6).map((k) => (
              <span
                key={k}
                className="max-w-full break-words rounded-md border border-border bg-muted/30 px-1.5 py-0.5 text-[10px]"
              >
                {k}
              </span>
            ))}
          </div>
        ) : null}
      </RailSection>

      <RailSection title="Company details">
        <dl className="space-y-2 text-xs">
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Employees</dt>
            <dd className="font-medium">
              {employeeCount != null ? employeeCount.toLocaleString() : "—"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Founded</dt>
            <dd className="font-medium">{foundedYear ?? "—"}</dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">HQ</dt>
            <dd className="min-w-0 break-words text-right font-medium">
              {locationLabel || "—"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Phone</dt>
            <dd className="min-w-0 break-all font-medium">
              {phone ? (
                <a href={`tel:${phone}`} className="text-brand hover:underline">
                  {phone}
                </a>
              ) : (
                "—"
              )}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Domain</dt>
            <dd className="min-w-0 break-all font-medium">
              {website || domain ? (
                <a
                  href={
                    website?.startsWith("http")
                      ? website
                      : `https://${domain ?? website}`
                  }
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline"
                >
                  {domain ?? website}
                </a>
              ) : (
                "—"
              )}
            </dd>
          </div>
          {linkedinUrl ? (
            <div className="flex min-w-0 justify-between gap-3">
              <dt className="shrink-0 text-muted-foreground">Social</dt>
              <dd>
                <a
                  href={linkedinUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="text-brand hover:underline"
                >
                  LinkedIn
                </a>
              </dd>
            </div>
          ) : null}
        </dl>
      </RailSection>

      <RailSection title="Record">
        <dl className="space-y-2 text-xs">
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Stage</dt>
            <dd className="min-w-0 break-words capitalize font-medium">
              {user?.status ?? user?.accountStage ?? directoryStage(merged) ?? "—"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Owner</dt>
            <dd className="min-w-0 break-words text-right font-medium">
              {user?.ownerName ?? user?.accountOwner ?? "—"}
            </dd>
          </div>
          <div className="flex min-w-0 justify-between gap-3">
            <dt className="shrink-0 text-muted-foreground">Priority</dt>
            <dd className="capitalize font-medium">{user?.priority ?? "—"}</dd>
          </div>
        </dl>
      </RailSection>

      <RailSection title="External links">
        <ExternalLinksList links={user?.externalLinks} />
      </RailSection>

      <RailSection title="Deals">
        {opps.length ? (
          <ul className="space-y-1">
            {opps.slice(0, 5).map((o) => (
              <li key={o.id} className="min-w-0 break-words">
                <Link
                  to={`/deals/${o.opportunityId ?? o.id}`}
                  className="break-words text-brand hover:underline"
                >
                  {o.dealName || o.opportunityId || o.id}
                </Link>
                {o.stage ? (
                  <span className="ml-1 break-words text-[11px] text-muted-foreground">
                    · {o.stage}
                  </span>
                ) : null}
              </li>
            ))}
            {opps.length > 5 ? (
              <li className="text-[11px] text-muted-foreground">
                +{opps.length - 5} more
              </li>
            ) : null}
          </ul>
        ) : (
          <p className="text-muted-foreground">No deals associated</p>
        )}
      </RailSection>

      <RailSection title="People">
        <p className="font-medium">
          {people.length} contact{people.length === 1 ? "" : "s"}
        </p>
      </RailSection>

      <RailSection title="Notes">
        {user?.notes?.trim() ? (
          <p className="whitespace-pre-wrap break-words text-muted-foreground">
            {user.notes}
          </p>
        ) : (
          <p className="text-muted-foreground">No notes yet</p>
        )}
      </RailSection>
    </aside>
  );
}

function directoryStage(merged: MergedAccountView): string | undefined {
  return merged.directory?.accountStage;
}
